import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { UserService, OrganizationService, AppService, HelperService, FitnessService, FoodService, RecipeService, MealPlanService, CheckinService, IFoodPreferences, StorageService, TranslateConfigService } from 'src/app/services';
import { ModalController, IonSelect, AlertController, ToastController, PopoverController } from '@ionic/angular';
import { MealPlanSettingsPage } from './meal-plan-settings/meal-plan-settings.page';
import { Organization, MealPlan, Recipe, Food, IMealPlanDetail, User, IMeal, IMenuLibraryItem } from 'src/app/models';
import { NotesPage } from 'src/app/components/notes/notes.page';
import { omit, safeFind, safeFindIndex } from 'src/app/utils';
import * as Sentry from "@sentry/angular-ivy";
import { NgForm } from '@angular/forms';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import moment from 'src/app/utils/moment';
import { MealSelectPage } from 'src/app/modals/meal-select/meal-select.page';
import { getProgramDatePopoverSide, ProgramDatePopoverComponent } from 'src/app/components/program-date-popover/program-date-popover.component';
import { MenuSelectComponent } from 'src/app/modals/menu-select/menu-select.component';
import { isAccountDeactivatedForBillingStatus } from 'src/app/shared/billing/organization-status-policy';

@Component({
  selector: 'app-meal-plan-builder',
  templateUrl: './meal-plan-builder.page.html',
  styleUrls: ['./meal-plan-builder.page.scss'],
})
export class MealPlanBuilderPage implements OnInit, AfterViewInit {
  @Input() comparePaneEmbedded = false;
  @Output() comparePaneClose = new EventEmitter<any>();

  @Input() modalContext?: {
    mealPlanUser?: string;
    userId?: string;
    id?: string;
    type?: 'user' | 'library';
    duplicate?: string;
    mealPlanType?: string;
    mode?: 'mealPlan' | 'menuLibrary';
  };

  public activeMeals = {};
  public actionSheet: HTMLIonActionSheetElement;
  public activePlan: any = { meals: [], totlas: {} };
  public activePlanIndex: number;
  public activeMealPlan: IMealPlanDetail = new MealPlan({ notes: [] }).cloneActive();
  public activeUnit: "metric" | "imperial";
  public activeUser: User;
  public configSet: boolean = false;
  public foods: Food[] = [];
  public hasDraft = false;
  public newSetType: string = null;
  public openFoods = {};
  public isPremium = false;
  public programUser: User;
  public programUserId: string;
  public programId: string;
  public programType: "user" | "library";
  public programMode: "mealPlan" | "menuLibrary" = "mealPlan";
  public recipes: Recipe[] = [];
  public setSelectOptions = {
    header: "Select Set Type"
  }
  public isShown = false;
  public showMacros: boolean = true;
  public showDetails = false;
  public showMeasurements = false;
  public showTotals = true;
  public mealPlan: MealPlan;
  public organization: Organization;
  public saveMessageEnabled = true;
  public totalPercent = 100;
  public userFoodPreferences: IFoodPreferences;
  public versionHistory = [];
  public userDetailsCollapsed = false;
  public isActivePlanPopoverOpen = false;
  public footerDrawerExpanded = false;
  private footerDrawerTouchStartY: number = null;

  get isMacroPlan(): boolean {
    return this.activeMealPlan?.type === 'macro';
  }

  get allMacroTargetsSet(): boolean {
    const g = this.activePlan?.goals;
    if (!g) return false;
    return (g.protein != null && g.protein > 0) && (g.fat != null && g.fat > 0) &&
      (g.carbs != null && g.carbs > 0) && (g.calories != null && g.calories > 0);
  }

  @ViewChild(IonSelect, { read: IonSelect }) select: IonSelect;
  @ViewChild('macroForm', { static: false }) public macroForm: NgForm;
  @ViewChild('userDetailsForm', { static: false }) public userDetailsForm: NgForm;
  constructor(private alertController: AlertController,
    public appService: AppService,
    private fitnessService: FitnessService,
    private foodService: FoodService,
    private helperService: HelperService,
    private mealPlanService: MealPlanService,
    private modalCtrl: ModalController,
    private organizationService: OrganizationService,
    private popoverCtrl: PopoverController,
    private checkinService: CheckinService,
    public recipeService: RecipeService,
    private storageService: StorageService,
    private toastCtrl: ToastController,
    private userService: UserService,
    private translateConfig: TranslateConfigService
  ) {

  }

  ngOnInit() {
    this.activeUser = this.userService.getAppUser();
    this.storageService.get("mealPlanBuilderMessageDisabled").then((val) => {
      if(val === true) {
        this.saveMessageEnabled = false;
      }
    }); 
  }

  /** Entry props from `MealPlanBuilderModalService` only. */
  private buildProgramEntrySource(): Record<string, any> {
    const ctx = { ...((this.modalContext || {}) as Record<string, any>) };
    if (!ctx.mealPlanUser && ctx.userId) {
      ctx.mealPlanUser = ctx.userId;
    }
    return ctx;
  }

  async ionViewDidEnter() {
    if (this.comparePaneEmbedded) return;
    await this.runMealPlanBuilderEntry();
  }

  ngAfterViewInit(): void {
    if (this.comparePaneEmbedded) {
      void this.runMealPlanBuilderEntry();
    }
  }

  private dismissBuilder(data?: any): void {
    if (this.comparePaneEmbedded) {
      this.comparePaneClose.emit(data);
      return;
    }
    void this.modalCtrl.dismiss(data);
  }

  private async runMealPlanBuilderEntry() {
    this.activeUnit = this.userService.getAppUser().preferedUnits || "imperial";
    const sourceParams = this.buildProgramEntrySource();
    this.organization = this.organizationService.getAppOrganization();
    if (isAccountDeactivatedForBillingStatus(this.organization?.status)) {
      await this.appService.showToastWithDuration(this.translateConfig.t('general.accountDeactivatedCannotEdit'), null, "danger");
      this.dismissBuilder();
      return;
    }

    this.programType = (sourceParams.type as 'user' | 'library') || "user";
    this.programMode = (sourceParams.mode as 'mealPlan' | 'menuLibrary') || 'mealPlan';
    this.programUserId = sourceParams.mealPlanUser;
    if (this.programUserId) {
      this.programUser = this.programUserId === this.activeUser.uid ? this.activeUser : await this.userService.getUserAsPromise(this.programUserId);
    } else {
      this.programUser = this.activeUser;
    }
    this.programId = sourceParams.id || "add";

    try {
      let shouldPromptMenuTitle = false;
      if(this.programUserId) {
        this.loadFoodPreferences(this.programUserId);
      }
      let activeMealPlan: IMealPlanDetail;
      const duplicateId = sourceParams.duplicate;
      const mealPlanTypeParam = sourceParams.mealPlanType;
      if (this.programMode === "menuLibrary") {
        this.configSet = true;
        if (this.programId === 'add') {
          if (duplicateId) {
            const duplicateMenu = await this.mealPlanService.getMenuAsPromise(this.organization.uid, duplicateId);
            const duplicatePlan = this.cloneMenuPlan(duplicateMenu);
            activeMealPlan = new MealPlan({
              title: duplicateMenu?.title || this.getDefaultMenuTitle(),
              plans: [duplicatePlan],
              notes: [],
              stats: {},
              type: 'meal',
            }).cloneActive();
          } else {
            activeMealPlan = new MealPlan({
              title: this.getDefaultMenuTitle(),
              plans: [this.createEmptyPlan(this.getDefaultMenuTitle())],
              notes: [],
              stats: {},
              type: 'meal',
            }).cloneActive();
            shouldPromptMenuTitle = true;
          }
        } else {
          const existingMenu = await this.mealPlanService.getMenuAsPromise(this.organization.uid, this.programId);
          if (!existingMenu) {
            throw new Error('menu_not_found');
          }
          activeMealPlan = new MealPlan({
            title: existingMenu.title || this.getDefaultMenuTitle(),
            plans: [this.cloneMenuPlan(existingMenu)],
            notes: [],
            stats: {},
            type: 'meal',
          }).cloneActive();
        }
      } else if (this.programId === "add") {
        if (duplicateId) {
          await this.appService.presentLoading();
          let mealPlan = this.programType === "library"? await this.mealPlanService.getOrganizationMealPlanAsPromise(this.organization.uid, duplicateId) : await this.mealPlanService.getMealPlanAsPromise(this.programUserId, duplicateId);
          activeMealPlan = mealPlan.hasDraft() ? mealPlan.cloneDraft() : mealPlan.cloneActive();
          activeMealPlan.title = activeMealPlan.title;
          delete activeMealPlan.startDate;
          delete activeMealPlan.endDate;
          activeMealPlan.stats = {};
        } else {
          activeMealPlan = new MealPlan({
            stats: {},
            notes: this.organization?.settings?.programs?.mealPlans?.defaultNotes || [],
            type: mealPlanTypeParam === 'macro' ? 'macro' : 'meal'
          }).cloneActive();
        }
        const defaultStartTime: string | null = this.organization?.getSetting("programs")?.defaultStartTime || null;
        const startMoment = (moment as any)();
        if (defaultStartTime) {
          const [hourStr] = defaultStartTime.split(":");
          const hour = parseInt(hourStr, 10);
          if (!Number.isNaN(hour)) {
            startMoment.hour(hour).minute(0).second(0).millisecond(0);
          }
        }
        activeMealPlan.startDate = startMoment.toISOString();
        activeMealPlan.startDateHidden = this.organization?.getSetting("programs")?.startDateHidden || false;
        activeMealPlan.endDateHidden = this.organization?.getSetting("programs")?.endDateHidden || false;
        // Here we set premium for tracking users
        if(!this.organization || !this.organization.isUserTrainer(this.activeUser.uid)) {
          this.isPremium = true;
          activeMealPlan.isPremium = true;
        }
      } else {
        this.configSet = true;
        if(this.programType === "user") {
          this.mealPlan = await this.mealPlanService.getMealPlanAsPromise(this.programUserId, this.programId);
        } else {
          this.mealPlan = await this.mealPlanService.getOrganizationMealPlanAsPromise(this.organization.uid, this.programId);
        }
        activeMealPlan = this.mealPlan.hasDraft() ? this.mealPlan.cloneDraft() : this.mealPlan.cloneActive();
        this.hasDraft = this.mealPlan.published && this.mealPlan.hasDraft();
      }
      await this.loadRecipes(activeMealPlan);
      await this.loadFoods(activeMealPlan);
      this.setVariations(activeMealPlan);
      if(this.programType === "user") {
        this.setStats(activeMealPlan);
      }
      if (activeMealPlan) {
        this.activeMealPlan = omit(activeMealPlan, ["createdBy", "creationDate"]);
        if (activeMealPlan.plans && activeMealPlan.plans.length > 0) {
          this.setActivePlan(0);
        }
        if (this.activeMealPlan?.type === 'macro') {
          this.isShown = true;
        }
        if (this.programType === "user") {
          this.setUserDetails();
        }
        const toSave = this.helperService.copyObject(this.activeMealPlan);
        this.versionHistory.push(toSave);
      }
      if (this.programMode === 'menuLibrary' && shouldPromptMenuTitle) {
        await this.promptMenuLibraryTitle(true);
      }
      this.appService.hideLoading();
      if (!this.configSet) {
        this.showProgramOptions();
      }
      if(this.programType === "user") {
        setTimeout(() => {
          this.setUserDetails();
          this.checkAutoCollapse();
        }, 100);
      }
    } catch (error) {
      console.log(error);
      Sentry.captureException(error.originalError || error);
      await this.appService.showError("An error has occurred. Our support team has been notified.");
    } finally {
      this.appService.hideLoading();
    }
  }

  async setStats(mp: IMealPlanDetail) {
    const activeAssessment = await this.checkinService.getMostRecentProgressReport(this.programUserId);
    mp.stats = mp.stats || {};
    mp.stats.age = mp.stats.age || this.programUser.getAge() || null;
    mp.stats.gender = mp.stats.gender || this.programUser.gender || null;
    mp.stats.activity = mp.stats.activity || (this.programUser.activityLevel ? this.helperService.activityLevelToValue(this.programUser.activityLevel) : null);
    mp.stats.bodyType = mp.stats.bodyType || (this.programUser.bodyType ? this.programUser.bodyType : null);
    mp.stats.bmr = mp.stats.bmr || null;     
    mp.stats.height = mp.stats.height || this.programUser.height || null;
    mp.stats.weight = mp.stats.weight || (activeAssessment && activeAssessment.weight? Math.round(activeAssessment.weight * 10) / 10 : null);
    mp.stats.bodyfat = mp.stats.bodyfat || (activeAssessment && activeAssessment.bodyfat? activeAssessment.bodyfat :null);

    if(this.activeUnit === "metric") {
      if(mp.stats.height) {
        mp.stats.height = this.helperService.inchesToCm(mp.stats.height)
      }
      if(mp.stats.weight) {
        mp.stats.weight = this.helperService.lbsToKg(mp.stats.weight)
      }
    }
  }

  loadFoodPreferences(userId: string) {
    this.userService.getFoodPreferences(userId).then((fp) => {
      this.userFoodPreferences = fp || {};
    });
  }

  getProgramOrganizationSetting(settingName: string) {
    return this.organization.settings.programs.workoutPrograms[settingName]
  }

  addPlan(title: string) {
    this.activeMealPlan.plans.push(this.createEmptyPlan(title));
    this.saveMealPlan();
  }

  private createEmptyPlan(title: string) {
    return {
      caloricAdjustment: 0,
      proteinFactor: 1.3,
      fatsFactor: 0.3,
      proteinPercent: 30,
      fatPercent: 30,
      carbPercent: 40,
      goals: {},
      title: title,
      meals: [
        {
          foods: [],
          title: ""
        }
      ]
    };
  }

  private cloneMenuPlan(plan: any) {
    const cloned = this.helperService.copyObject(plan || {});
    const title = cloned.title || this.getDefaultMenuTitle();
    return {
      ...this.createEmptyPlan(title),
      ...cloned,
      title,
      meals: Array.isArray(cloned.meals) ? cloned.meals : [],
      goals: cloned.goals || {},
    };
  }

  private getDefaultMenuTitle() {
    return `Menu ${(this.activeMealPlan?.plans?.length || 0) + 1}`;
  }

  setActivePlan(index: number, options: { closePopover?: boolean } = {}) {
    const plans = this.activeMealPlan.plans || [];
    const closePopover = options.closePopover ?? true;

    if (!plans.length) {
      this.activePlan = null;
      this.activePlanIndex = 0;
      this.activeMeals = {};
      if (closePopover) {
        this.isActivePlanPopoverOpen = false;
      }
      return;
    }

    const safeIndex = Math.max(0, Math.min(index, plans.length - 1));
    this.activePlan = plans[safeIndex];
    this.activePlan.goals = this.activePlan.goals || {};
    this.activePlanIndex = safeIndex;
    this.activeMeals = {};

    if (closePopover) {
      this.isActivePlanPopoverOpen = false;
    }
  }

  getPlanCalories = (plan: any): number => {
    return this.calculatePlanTotal(plan)?.calories || 0;
  }

  getMacroProgressPercent(used: number, target: number): number {
    if (!target || target <= 0) {
      return 0;
    }
    const percent = (used / target) * 100;
    return Math.max(0, Math.min(100, percent));
  }

  isOverMacroTarget(total: number | null | undefined, target: number | null | undefined): boolean {
    if (target == null) return false;
    return Math.round(total ?? 0) > Math.round(target);
  }

  formatCalories(value: number | null | undefined): string {
    if (value == null) return '—';
    return Math.round(value).toString();
  }

  async addMeal() {
    if (!this.activePlan) {
      return;
    }
    this.activePlan.meals = this.activePlan.meals || [];
    this.activePlan.meals.push({
      foods: [],
      title: ""
    });
    this.saveMealPlan();
  }

  async addSavedMeal() {
    const modal = await this.modalCtrl.create({
      backdropDismiss:false,
      component: MealSelectPage,
      componentProps: {}
    });
    await modal.present();
    const { data } = await modal.onWillDismiss();
    if (data && data.status === "selected") {
      if (!this.activePlan) {
        return;
      }
      let meal: IMeal = data.data;
      meal.foods = meal.foods || [];
      this.activePlan.meals = this.activePlan.meals || [];
      let index = this.activePlan.meals.length + 1;
      try {
        const promises = [];
        await this.appService.presentLoading();
        for (const food of meal.foods) {
          if (food.food && safeFindIndex(this.foods, f => f.id === food.food) === -1) {
            const prom = this.foodService.getFoodAsPromise(food.food).then((food) => {
              if(food) {
                this.foods.push(food);
              }
            });
            promises.push(prom);
          }
        }
        await Promise.all(promises);
        this.activePlan.meals.push({
          foods: meal.foods || null,
          notes: meal.notes || null,
          title: meal.title || "Meal " + index,
          options: meal.options || [],
        });
        this.setVariations(this.activeMealPlan);
      } catch (error) {
        console.log(error);
        Sentry.captureException(error.originalError || error);
        this.appService.showToastWithDuration("Unable to add meal. Please try again", null, "danger");
      } finally {
        this.appService.hideLoading();
      }
      this.saveMealPlan();
    }
  }

  async copyMealToMenu(meal) {
    const inputs = [];
    for (let i = 0; i < this.activeMealPlan.plans.length; i++) {
      const plan = this.activeMealPlan.plans[i];
      inputs.push({
        name: `plan-${i}`,
        type: 'checkbox',
        label: plan.title,
        value: i
      });
    }
    const alert = await this.alertController.create({
      header: 'Select Menu',
      inputs: inputs,
      buttons: [
        {
          text: this.translateConfig.t('general.cancel'),
          role: 'cancel',
          cssClass: 'secondary',
        }, {
          text: 'Copy',
          handler: (vals) => {
            if(vals?.length > 0) {
              for (const WI of vals) {
                this.activeMealPlan.plans[WI].meals = this.activeMealPlan.plans[WI].meals || []
                this.activeMealPlan.plans[WI].meals.push(this.helperService.copyObject(meal));
              }
            }
          }
        }
      ]
    });
    await alert.present();
  }

  setMealOption(mealIndex: number, optionIndex: number) {
    this.activeMeals[`meal-${mealIndex}`] = optionIndex;
  }

  isMealOptionActive(mealIndex: number, optionIndex: number) {
    return this.activeMeals[`meal-${mealIndex}`] === optionIndex;
  }

  addMealOption(index) {
    this.activePlan.meals[index].options = this.activePlan.meals[index].options || [];
    this.activePlan.meals[index].options.push({
      foods: [],
      title: ""
    });
    this.saveMealPlan();
  }

  isActiveFoodOption(mealIndex: number, foodObj: any) {
    const activeIndex = this.activeMeals[`meal-` + mealIndex]
    return  this.helperService.valueExists(activeIndex)? activeIndex === foodObj.option : !this.helperService.valueExists(foodObj.option);
  }

  async editPlanTitle() {
    let value = this.activePlan.title;
    const alert = await this.alertController.create({
      header: 'Edit Menu Title',
      //cssClass: "alertctrl-wide",
      inputs: [
        {
          name: 'planTitle',
          type: 'text',
          value: value,
          placeholder: `e.g. Non Workout Days`,
        }
      ],
      buttons: [
        {
          text: this.translateConfig.t('general.cancel'),
          role: 'cancel',
          cssClass: 'secondary',
          handler: () => { }
        }, {
          text: this.translateConfig.t('general.ok'),
          handler: async (values) => {
            let planTitle = values.planTitle
            if (planTitle) {
              this.activePlan.title = planTitle;
              this.saveMealPlan();
            } else {
              return
            }
          }
        }
      ]
    });
    await alert.present();
  }

  async editMeal(index) {
    let value = this.activePlan.meals[index].title;
    const alert = await this.alertController.create({
      header: 'Edit Meal Title',
      cssClass: "mealTitleAlert",
      inputs: [
        {
          name: 'mealTitle',
          type: 'textarea',
          value: value,
          placeholder: `e.g. Meal ${index}`,
        }
      ],
      buttons: [
        {
          text: this.translateConfig.t('general.cancel'),
          role: 'cancel',
          cssClass: 'secondary',
          handler: () => { }
        }, {
          text: this.translateConfig.t('general.ok'),
          handler: async (values) => {
            let mealTitle = values.mealTitle
            this.activePlan.meals[index].title = mealTitle || "";
            this.saveMealPlan();
          }
        }
      ]
    });
    await alert.present();
  }

  duplicateMeal(index) {
    const toCopy = this.helperService.copyObject(this.activePlan.meals[index]);
    toCopy.title = this.activePlan.meals[index].title
    this.activePlan.meals.push(toCopy);
    this.saveMealPlan();
  }

  async removeMeal(index) {
    const alert = await this.alertController.create({
      header: this.translateConfig.t('general.confirm'),
      message: 'Are you sure you want to delete this meal?',
      cssClass: 'secondary',
      buttons: [
        {
          text: this.translateConfig.t('general.cancel'),
          role: 'cancel',
          cssClass: 'secondary',
          handler: (blah) => { }
        }, {
          text: this.translateConfig.t('general.delete'),
          handler: async () => {
            this.activePlan.meals.splice(index, 1);
            this.activeMeals = {};
            this.saveMealPlan();
          }
        }
      ]
    });
    await alert.present();
  }

  reorderMeal(event) {
    const itemMove = this.activePlan.meals.splice(event.detail.from, 1)[0];
    this.activePlan.meals.splice(event.detail.to, 0, itemMove);
    delete this.activeMeals[`meal-` + event.detail.to];
    delete this.activeMeals[`meal-` + event.detail.from];
    this.saveMealPlan();
    event.detail.complete();
  }

  async newPlan(options: { closePopover?: boolean } = {}) {
    const alert = await this.alertController.create({
      header: 'Menu Title',
      inputs: [
        {
          name: 'planTitle',
          type: 'text',
          placeholder: 'e.g. Workout Days'
        }
      ],
      buttons: [
        {
          text: this.translateConfig.t('general.cancel'),
          role: 'cancel',
          cssClass: 'secondary',
          handler: () => { }
        }, {
          text: this.translateConfig.t('general.ok'),
          handler: async (values) => {
            if (values.planTitle) {
              this.addPlan(values.planTitle);
              const closePopover = options.closePopover ?? true;
              this.setActivePlan(this.activeMealPlan.plans.length - 1, { closePopover });
              this.saveMealPlan();
            } else {
              const failAert = await this.alertController.create({
                header: 'Menu Title Missing',
                message: 'You must enter a menu title',
                buttons: ['OK']
              });
              await failAert.present();
            }
          }
        }
      ]
    });
    await alert.present();
  }

  addBlankPlan(options: { closePopover?: boolean } = {}) {
    const closePopover = options.closePopover ?? true;
    this.addPlan(this.getDefaultMenuTitle());
    this.setActivePlan(this.activeMealPlan.plans.length - 1, { closePopover });
  }

  getProgramSettings() {
    return this.helperService.copyObject({
      title: this.activeMealPlan.title || null,
      startDate: this.activeMealPlan.startDate || null,
      startDateHidden: this.helperService.valueExists(this.activeMealPlan.startDateHidden)? this.activeMealPlan.startDateHidden : false,
      endDate: this.activeMealPlan.endDate || null,
      endDateHidden: this.helperService.valueExists(this.activeMealPlan.endDateHidden)? this.activeMealPlan.endDateHidden : false,
      plans: this.activeMealPlan.plans || [],
    });
  }

  setProgramSettings(obj) {
    Object.assign(this.activeMealPlan, obj);
  }

  async showProgramOptions() {
    let action = this.configSet ? "edit" : "add";
    const modal = await this.modalCtrl.create({
      backdropDismiss:false,
      component: MealPlanSettingsPage,
      componentProps: {
        action: action,
        mealPlan: this.getProgramSettings(),
        type: this.programType,
        premium: this.isPremium
      }
    });
    await modal.present();
    const { data } = await modal.onWillDismiss();
    if (data && data.status === 'submitted') {
      this.setProgramSettings(data.data);
      this.configSet = true;
      this.setActivePlan(
        !this.activeMealPlan.plans?.length
          ? 0
          : (isNaN(this.activePlanIndex) ? 0 : this.activePlanIndex),
      );
      this.saveMealPlan();
    } else if (!this.configSet) {
      setTimeout(() => {
        this.dismissBuilder();
      }, 500);
    } else { }
  }

  async onHeaderTitleClick() {
    if (this.programMode === 'menuLibrary') {
      await this.promptMenuLibraryTitle(false);
      return;
    }
    await this.showProgramOptions();
  }

  private async promptMenuLibraryTitle(required = false) {
    const currentTitle = this.activeMealPlan?.title || this.activePlan?.title || '';
    const alert = await this.alertController.create({
      header: 'Menu Title',
      inputs: [
        {
          name: 'menuTitle',
          type: 'text',
          value: currentTitle,
          placeholder: 'e.g. Workout Days Menu',
        }
      ],
      buttons: [
        {
          text: this.translateConfig.t('general.cancel'),
          role: 'cancel',
          cssClass: 'secondary',
          handler: () => {
            if (required) {
              this.dismissBuilder();
            }
          }
        },
        {
          text: this.translateConfig.t('general.save'),
          handler: async (values) => {
            const menuTitle = (values?.menuTitle || '').trim();
            if (!menuTitle) {
              await this.appService.showToastWithDuration('You must enter a menu title.', null, 'danger');
              return false;
            }
            this.activeMealPlan.title = menuTitle;
            if (this.activeMealPlan?.plans?.[0]) {
              this.activeMealPlan.plans[0].title = menuTitle;
            }
            await this.saveMealPlan();
            return true;
          }
        }
      ]
    });
    await alert.present();
  }

  async showNotes() {
    let notes = this.activeMealPlan.notes;
    const modal = await this.modalCtrl.create({
      backdropDismiss:false,
      component: NotesPage,
      componentProps: {
        defaultNotes: this.organization?.settings?.programs?.mealPlans?.defaultNotes,
        action: "edit",
        notes: notes
      }
    });
    await modal.present();
    const { data } = await modal.onWillDismiss();
    if (data && data.status === "submitted") {
      this.activeMealPlan.notes = data.data;
      this.saveMealPlan();
    }
  }

  async editMealNotes(index) {
    let notes = this.activePlan.meals[index].notes || [];
    const modal = await this.modalCtrl.create({
      backdropDismiss:false,
      component: NotesPage,
      componentProps: {
        action: "edit",
        notes: notes
      }
    });
    await modal.present();
    const { data } = await modal.onWillDismiss();
    if (data && data.status === "submitted") {
      this.activePlan.meals[index].notes = data.data;
      this.saveMealPlan();
    }
  }

  disableSaveMessage() {
    this.storageService.set("mealPlanBuilderMessageDisabled", true);
    this.saveMessageEnabled = false;
  }

  async showSaveMessage() {
    if(!this.saveMessageEnabled) {
      return
    }
    const toast = await this.toastCtrl.create({
      message: "Your meal plan has been automatically saved",
      buttons: [
        {
          text: "Don't show again",
          role: 'cancel',
          handler: () => {
            this.disableSaveMessage();
          }
        }
      ],
      position: 'bottom',
      color: "success",
      duration: 3000,
    });
    toast.present();
  }

  async saveMealPlan() {
    try {
      let res;
      const toSave = this.helperService.copyObject(this.activeMealPlan);
      this.versionHistory.push(toSave);
      if (this.programMode === 'menuLibrary') {
        const menuSource = this.activeMealPlan?.plans?.[0] || this.activeMealPlan?.plans?.[this.activePlanIndex ?? 0];
        const activeMenu = this.cloneMenuPlan(menuSource);
        this.activeMealPlan.plans = [activeMenu];
        this.setActivePlan(0, { closePopover: true });
        const menuPayload: IMenuLibraryItem = {
          ...this.helperService.cleanObjectForFirebase(activeMenu),
          title: this.activeMealPlan.title || activeMenu.title || this.getDefaultMenuTitle(),
        };
        if (this.programId === "add") {
          res = await this.mealPlanService.addMenu(this.organization?.uid, this.activeUser.uid, {
            ...menuPayload,
            lastModifiedBy: this.activeUser.uid,
            lastModifiedDate: new Date().toISOString(),
          });
        } else {
          res = await this.mealPlanService.updateMenu(this.organization?.uid, this.programId, {
            ...menuPayload,
            lastModifiedBy: this.activeUser.uid,
            lastModifiedDate: new Date().toISOString(),
          });
        }
      } else
      if(this.programType === "user") {
        if(this.activeUnit === "metric") {
          if(toSave.stats?.height) {
            toSave.stats.height = this.helperService.cmToInches(this.activeMealPlan.stats.height);
          }
          if(toSave.stats?.weight) {
            toSave.stats.weight = this.helperService.kgToLbs(this.activeMealPlan.stats.weight);
          }
        }
        res = await this.mealPlanService.saveUserMealPlan(this.organization?.uid, this.programUserId, this.programId, toSave, this.activeUser.uid);
      } else {
        res = await this.mealPlanService.saveOrganizationMealPlan(this.organization?.uid, this.programId, toSave, this.activeUser.uid);
      }
      if (this.programId === "add") {
        this.programId = res.key;
      }
      this.showSaveMessage();
      if(this.mealPlan?.published) {
        this.hasDraft = true;
      }
    } catch (error) {
      console.log(error);
      this.appService.showToastWithDuration(this.translateConfig.t('general.unexpectedError'));
      Sentry.captureException(error.originalError || error);
    }
  }

  async publishMealPlan() {
    if (this.programMode === 'menuLibrary') {
      await this.saveMealPlan();
      this.appService.showToastWithDuration("Menu saved to library", null, "success");
      this.dismissBuilder({ status: 'saved', programId: this.programId });
      return;
    }
    await this.appService.presentLoading();
    try {     
      let programValue;
      const toSave = this.helperService.copyObject(this.activeMealPlan);
      if(this.programType === "user") {
        if(this.activeUnit === "metric") {
          if(toSave.stats?.height) {
            toSave.height = this.helperService.cmToInches(toSave.stats.height);
          }
          if(toSave.stats?.weight) {
            toSave.stats.weight = this.helperService.kgToLbs(toSave.stats.weight);
          }
        }
        programValue = await this.mealPlanService.publishUserMealPlan(this.organization?.uid, this.programUserId, this.programId, toSave, this.activeUser.uid);
      } else {
        programValue = await this.mealPlanService.publishOrganizationMealPlan(this.organization.uid, this.programId, toSave, this.activeUser.uid);
      }
      if(this.programId === "add") {
        this.programId = programValue.key;
      }

      this.dismissBuilder({ status: 'published', programId: this.programId });

      this.appService.showToastWithDuration("Successfully published meal plan", null, "success");       
    } catch (error) {
      Sentry.captureException(error.originalError || error);
      this.appService.showToastWithDuration(this.translateConfig.t('general.unexpectedError'), null, "danger");
    } finally {
      await this.appService.hideLoading();
    }
  }

  undoRevision() {
    const historyIndex = this.versionHistory.length - 1;
    this.activeMealPlan = this.versionHistory[historyIndex - 1];
    this.setActivePlan(this.activePlanIndex);
    this.versionHistory.splice(historyIndex -1, 2);
    this.saveMealPlan();
  }

  async revertDraft() {
    await this.appService.presentLoading();
    let activeMealPlan = this.mealPlan.cloneActive();
    this.setStats(activeMealPlan);
    await this.loadRecipes(activeMealPlan);
    await this.loadFoods(activeMealPlan);
    this.setVariations(activeMealPlan);
    if (activeMealPlan) {
      this.activeMealPlan = activeMealPlan;
      if (activeMealPlan.plans && activeMealPlan.plans.length > 0) {
        this.setActivePlan(0);
      }
      const toSave = this.helperService.copyObject(this.activeMealPlan);
      this.versionHistory = [toSave];
    }
    try {     
      if(this.programType === "user") {
        await this.mealPlanService.publishUserMealPlan(this.organization.uid, this.programUserId, this.programId, this.activeMealPlan, this.activeUser.uid);
      } else {
        await this.mealPlanService.publishOrganizationMealPlan(this.organization.uid, this.programId, this.activeMealPlan, this.activeUser.uid);
      }
      this.hasDraft = false;
      this.appService.showToastWithDuration("Successfully reverted meal plan", null, "success");       
    } catch (error) {
      console.log(error);
      Sentry.captureException(error.originalError || error);
      this.appService.showToastWithDuration(this.translateConfig.t('general.unexpectedError'), null, "danger");
    } finally {
      await this.appService.hideLoading();
    }
  }

  /* 
  ** Meal Plan functions
  ** - To clean up
  */
  getFood(foodId, type = "food"): any {
    if (type === "recipe") {
      return safeFind(this.recipes, r => r.uid === foodId)
    } else {
      return safeFind(this.foods, f => f.id === foodId)
    }
  }

  async loadRecipes(mp: IMealPlanDetail) {
    const recipeIds = this.mealPlanService.getMealPlanRecipeIds(mp);
    const promises = [];
    for (const recipeId of recipeIds) {
      const index = safeFindIndex(this.recipes, r => r.uid === recipeId);
      if(index === -1) {
        const prom = this.recipeService.getRecipeAsPromise(recipeId).then((recipe) => {
          if(recipe) {
            this.recipes.push(recipe);
          }
        });
        promises.push(prom);
      }
    }
    await Promise.all(promises);
  }

  setVariations(mealPlan: IMealPlanDetail) {
    if(mealPlan.plans) {
      for (const plan of mealPlan.plans) {
        if(plan.meals) {
          for (const meal of plan.meals) {
            if(meal.foods) {
              for (const foodObj of meal.foods) {
                if(!foodObj.variationUnit && !foodObj.variationQuantity) {
                  const food = this.getFood(foodObj.food, foodObj.type);
                  if(food) {
                    if(foodObj.type === "recipe") {
                      foodObj.variationQuantity = foodObj.quantity || null;
                      foodObj.variationUnit = "portion";
                    } else {
                      foodObj.variationQuantity = foodObj.quantity || null;
                      foodObj.variationUnit = food.defaultUnit || null;
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  async loadFoods(mealPlan: IMealPlanDetail) {
    const foodIds = this.mealPlanService.getFoodIds(mealPlan);
    for (const recipe of this.recipes) {
      const recipeFoods = recipe.getFoods();
      for (const foodId of recipeFoods) {
        if (foodIds.indexOf(foodId) === -1) {
          foodIds.push(foodId);
        }
      }
    }
    const promises = [];
    for (const foodId of foodIds) {
      if (safeFindIndex(this.foods, f => f.id === foodId) === -1) {
        const prom = this.foodService.getFoodAsPromise(foodId)
        .then((food) => {
          this.foods.push(food);
        }).catch(() => {});
        promises.push(prom);
      }
    }
    await Promise.all(promises);
  }

  calculateProportions(value, initial, final) {
    return value / initial * final
  }

  calculateMealTotal(meal, index) {
    let totals = {
      protein: 0,
      fat: 0,
      carbs: 0,
      sugar: 0,
      fiber: 0,
      calories: 0
    };
    if (meal.foods) {
      for (const foodObj of meal.foods) {
        const food = this.getFood(foodObj.food, foodObj.type);
        if (food && foodObj.quantity && this.isActiveFoodOption(index, foodObj)) {
          totals.protein = totals.protein + food.getMacro("protein", foodObj.quantity, this.foods);
          totals.fat = totals.fat + food.getMacro("fat", foodObj.quantity, this.foods);
          totals.carbs = totals.carbs + food.getMacro("carbs", foodObj.quantity, this.foods);
          totals.sugar = totals.sugar + food.getMacro("sugar", foodObj.quantity, this.foods);
          totals.fiber = totals.fiber + food.getMacro("fiber", foodObj.quantity, this.foods);
          totals.calories = totals.calories + food.getMacro("calories", foodObj.quantity, this.foods);
        }
      }
    }
    return totals
  }

  calculatePlanTotal(plan) {
    let totals = {
      protein: 0,
      fat: 0,
      carbs: 0,
      sugar: 0,
      fiber: 0,
      calories: 0
    }
    if (!plan || !plan.meals || plan.meals.length === 0) {
      return totals;
    }
    let i = 0;
    for (const meal of plan.meals) {
      let mealTotal = this.calculateMealTotal(meal, i);
      totals.protein += mealTotal.protein;
      totals.fat += mealTotal.fat;
      totals.carbs += mealTotal.carbs;
      totals.sugar += mealTotal.sugar;
      totals.fiber += mealTotal.fiber;
      totals.calories += mealTotal.calories;
      i++;
    }
    return totals
  }

  getCaloricProportion(macro: number, type: "p" | "c" | "f") {
    if (!this.activePlan) {
      return null;
    }
    const PT = this.calculatePlanTotal(this.activePlan).calories;
    if (macro === null || macro === undefined || PT === 0) {
      return null
    }
    switch (type) {
      case "p":
        return ((macro * 4) / PT * 100).toFixed(0);
      case "c":
        return ((macro * 4) / PT * 100).toFixed(0);
      case "f":
        return ((macro * 9) / PT * 100).toFixed(0);
      default:
        break;
    }
  }

  macroChanged() {
    if (!this.activePlan?.goals) {
      return;
    }
    this.activePlan.goals.calories = ((this.activePlan.goals.protein || 0) * 4) + ((this.activePlan.goals.carbs || 0) * 4) + ((this.activePlan.goals.fat || 0) * 9);
  }

  async setUserDetails() {
    if (!this.activeMealPlan || !this.activeMealPlan.stats) {
      return
    }
    if (this.hasRequiredUserStats()) {
      this.activeMealPlan.stats.bmr = this.fitnessService.calculateBMRMSJE(this.activeMealPlan.stats, this.activeUnit);
      this.activeMealPlan.stats.maintenance = this.activeMealPlan.stats.bmr * this.activeMealPlan.stats.activity * this.helperService.bodyTypeToValue(this.activeMealPlan.stats.bodyType);
      await this.userService.updateDBUser(this.programUserId, {bodyType: <any>this.activeMealPlan.stats.bodyType, activityLevel: <any>this.helperService.activityValueToLevel(this.activeMealPlan.stats.activity)});
    } else {
      this.activeMealPlan.stats.bmr = null;
      this.activeMealPlan.stats.maintenance = null;
    }
  }

  private hasRequiredUserStats(): boolean {
    const stats = this.activeMealPlan?.stats;
    return !!(
      stats &&
      stats.age &&
      stats.gender &&
      stats.height &&
      stats.weight &&
      stats.activity &&
      stats.bodyType
    );
  }

  checkAutoCollapse() {
    if (this.hasRequiredUserStats()) {
      this.userDetailsCollapsed = true;
    }
  }

  toggleUserDetails() {
    this.userDetailsCollapsed = !this.userDetailsCollapsed;
  }

  isFieldIncomplete(fieldName: string): boolean {
    const stats = this.activeMealPlan?.stats;
    if (!stats) return true;
    
    switch (fieldName) {
      case 'age':
        return !stats.age;
      case 'gender':
        return !stats.gender;
      case 'height':
        return !stats.height;
      case 'weight':
        return !stats.weight;
      case 'bodyType':
        return !stats.bodyType;
      case 'activity':
        return !stats.activity;
      default:
        return false;
    }
  }

  updateTargetMacro(label: string, event: any) {
    if (!this.activePlan?.goals) {
      return;
    }
    const value = parseFloat(event.target.value) || 0;
    
    switch (label) {
      case 'Proteins':
        this.activePlan.goals.protein = value;
        break;
      case 'Fats':
        this.activePlan.goals.fat = value;
        break;
      case 'Carbs':
        this.activePlan.goals.carbs = value;
        break;
      case 'Calories':
        this.activePlan.goals.calories = value;
        break;
    }
    
    this.macroChanged();
  }

  private getRoundedValue(value: number): number {
    const rounded = Math.round(value || 0);
    return Object.is(rounded, -0) ? 0 : rounded;
  }

  getTargetMacrosRemaining() {
    if (!this.activePlan?.goals) return [];
    
    const planTotals = this.calculatePlanTotal(this.activePlan);
    const goals = this.activePlan.goals;

    const proteinRemaining = this.getRoundedValue((goals.protein || 0) - (planTotals.protein || 0));
    const fatRemaining = this.getRoundedValue((goals.fat || 0) - (planTotals.fat || 0));
    const carbsRemaining = this.getRoundedValue((goals.carbs || 0) - (planTotals.carbs || 0));
    const caloriesRemaining = this.getRoundedValue((goals.calories || 0) - (planTotals.calories || 0));
    
    const macros = [
      {
        label: 'Proteins',
        target: `${goals.protein || 0}g`,
        percentage: goals.calories ? `${((goals.protein || 0) * 4 / goals.calories * 100).toFixed(0)}%` : '-',
        remainingNum: proteinRemaining,
        remaining: `${proteinRemaining}g`
      },
      {
        label: 'Fats',
        target: `${goals.fat || 0}g`,
        percentage: goals.calories ? `${((goals.fat || 0) * 9 / goals.calories * 100).toFixed(0)}%` : '-',
        remainingNum: fatRemaining,
        remaining: `${fatRemaining}g`
      },
      {
        label: 'Carbs',
        target: `${goals.carbs || 0}g`,
        percentage: goals.calories ? `${((goals.carbs || 0) * 4 / goals.calories * 100).toFixed(0)}%` : '-',
        remainingNum: carbsRemaining,
        remaining: `${carbsRemaining}g`
      },
      {
        label: 'Calories',
        target: `${goals.calories || 0}`,
        percentage: '-',
        remainingNum: caloriesRemaining,
        remaining: `${caloriesRemaining}`
      }
    ];
    
    return macros;
  }

  trackByMacroLabel(index: number, macro: { label: string }) {
    return macro.label;
  }

  isTrainer() {
    return this.activeUser && this.organization.isUserTrainer(this.activeUser.uid);
  }

  toggleDetails() {
    this.showDetails = !this.showDetails;
  }

  toggleMeasurements() {
    this.showMeasurements = !this.showMeasurements;
  }

  setHeaderView(view: 'meals' | 'macros' | string) {
    this.isShown = view === 'macros';
  }

  toggleFooterDrawer(forceState?: boolean) {
    if (typeof forceState === 'boolean') {
      this.footerDrawerExpanded = forceState;
      return;
    }
    this.footerDrawerExpanded = !this.footerDrawerExpanded;
  }

  onFooterDrawerTouchStart(event: TouchEvent) {
    this.footerDrawerTouchStartY = event?.changedTouches?.[0]?.clientY ?? null;
  }

  onFooterDrawerTouchEnd(event: TouchEvent) {
    if (this.footerDrawerTouchStartY === null || this.footerDrawerTouchStartY === undefined) {
      return;
    }

    const endY = event?.changedTouches?.[0]?.clientY ?? this.footerDrawerTouchStartY;
    const deltaY = endY - this.footerDrawerTouchStartY;

    if (deltaY < -18) {
      this.footerDrawerExpanded = true;
    } else if (deltaY > 18) {
      this.footerDrawerExpanded = false;
    }

    this.footerDrawerTouchStartY = null;
  }

  openActivePlanDrawer() {
    this.isActivePlanPopoverOpen = !this.isActivePlanPopoverOpen;
  }

  closeActivePlanPopover() {
    this.isActivePlanPopoverOpen = false;
  }

  onPlanSelected(index: number) {
    this.setActivePlan(index);
  }

  selectPlanForActions(index: number) {
    this.setActivePlan(index, { closePopover: false });
  }

  editPlanFromMenu(index: number) {
    this.selectPlanForActions(index);
    this.editPlanTitle();
  }

  duplicatePlanFromMenu(index: number) {
    this.selectPlanForActions(index);
    this.duplicatePlan(index);
  }

  deletePlanFromMenu(index: number) {
    this.selectPlanForActions(index);
    this.deletePlan({ closePopover: false });
  }

  onCreatePlanFromPopover() {
    this.newPlan({ closePopover: true });
  }

  onCreateBlankPlanFromPopover() {
    this.addBlankPlan({ closePopover: true });
  }

  async addPlanFromLibrary() {
    if (this.isMacroPlan && this.programMode !== 'menuLibrary') {
      return;
    }
    const modal = await this.modalCtrl.create({
      backdropDismiss: false,
      component: MenuSelectComponent,
    });
    await modal.present();
    const { data } = await modal.onWillDismiss();
    if (data?.status === 'selected' && data?.data) {
      const importedPlan = this.cloneMenuPlan(data.data);
      if (!importedPlan.title) {
        importedPlan.title = data.data.title || this.getDefaultMenuTitle();
      }
      this.activeMealPlan.plans.push(importedPlan);
      await this.loadRecipes(this.activeMealPlan);
      await this.loadFoods(this.activeMealPlan);
      this.setVariations(this.activeMealPlan);
      this.setActivePlan(this.activeMealPlan.plans.length - 1);
      await this.saveMealPlan();
    }
  }

  onPlanReordered(event: CdkDragDrop<any[]>) {
    const selectedPlan = this.activePlan;
    moveItemInArray(this.activeMealPlan.plans, event.previousIndex, event.currentIndex);

    const selectedPlanIndex = this.activeMealPlan.plans.indexOf(selectedPlan);
    this.setActivePlan(selectedPlanIndex > -1 ? selectedPlanIndex : event.currentIndex, { closePopover: false });
    this.saveMealPlan();
  }

  openCalc() {
    this.isShown = !this.isShown;
  }

  toggleMacros() {
    this.showMacros = !this.showMacros;
  }

  toggleTotals() {
    this.showTotals = !this.showTotals;
  }

  /* Plan Options */
  duplicatePlan(currentIndex) {
    const toCopy = this.helperService.copyObject(this.activeMealPlan.plans[this.activePlanIndex]);
    toCopy.title = this.activeMealPlan.plans[this.activePlanIndex].title;
    this.activeMealPlan.plans.splice(currentIndex + 1, 0,toCopy);
    this.activePlanIndex = currentIndex + 1;
    this.saveMealPlan();
  }

  deletePlan(options: { closePopover?: boolean } = {}) {
    const plans = this.activeMealPlan.plans;
    if (!plans?.length || !plans[this.activePlanIndex]) {
      return;
    }
    const deletedIndex = this.activePlanIndex;
    plans.splice(deletedIndex, 1);
    const closePopover = options.closePopover ?? true;
    const remaining = plans.length;

    if (remaining === 0) {
      this.setActivePlan(0, { closePopover: false });
      this.isActivePlanPopoverOpen = true;
    } else {
      const nextIndex = Math.min(deletedIndex, remaining - 1);
      this.setActivePlan(Math.max(0, nextIndex), { closePopover });
    }
    this.saveMealPlan();
  }

  drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.activeMealPlan.plans, event.previousIndex, event.currentIndex);
    this.setActivePlan(event.currentIndex)
    this.saveMealPlan();
  }

  goBack() {
    this.dismissBuilder();
  }

  getDeficit() {
    return Math.round(Math.abs(this.activePlan.goals.calories - this.activeMealPlan.stats.maintenance));
  }

  async saveMealToLibrary(meal) {
    const toSave = this.helperService.copyObject(meal);
    let value = toSave.title;
    const alert = await this.alertController.create({
      message: "Set a title for this meal in your library",
      inputs: [
        {
          name: 'mealTitle',
          type: 'text',
          value: value,
          placeholder: `e.g. Meal `,
        }
      ],
      buttons: [
        {
          text: this.translateConfig.t('general.cancel'),
          role: 'cancel',
          cssClass: 'secondary',
          handler: () => { }
        }, {
          text: this.translateConfig.t('general.ok'),
          handler: async (values) => {
            let mealTitle = values.mealTitle
            if (mealTitle) {
              toSave.title = mealTitle;
              try {
                await this.mealPlanService.addMeal(this.activeUser.uid, {
                  ...this.helperService.cleanObjectForFirebase(toSave),
                  organization: this.organization?.isUserTrainer(this.activeUser.uid)? this.organization.uid : null,
                });
                this.appService.showToastWithDuration("Meal successfully saved to library", null, "success")
              } catch (error) {
                Sentry.captureException(error.originalError || error);
                this.appService.showToastWithDuration("Unable to save meal to library", null, "danger")
              }
            } else {
              await this.appService.showToastWithDuration("You must provide a name for this meal", null, "danger");
            }
          }
        }
      ]
    });
    await alert.present();
  }

  async saveMenuToLibrary(index: number = this.activePlanIndex) {
    if (this.isMacroPlan && this.programMode !== 'menuLibrary') {
      return;
    }
    const plan = this.activeMealPlan?.plans?.[index];
    if (!plan) {
      return;
    }

    const toSave = this.cloneMenuPlan(plan);
    // Preserve target macro goals from the source plan when saving to menu library.
    toSave.goals = this.helperService.copyObject(plan?.goals || toSave.goals || {});
    const alert = await this.alertController.create({
      message: "Set a title for this menu in your library",
      inputs: [
        {
          name: 'menuTitle',
          type: 'text',
          value: toSave.title || this.getDefaultMenuTitle(),
          placeholder: 'e.g. Workout Days Menu',
        }
      ],
      buttons: [
        {
          text: this.translateConfig.t('general.cancel'),
          role: 'cancel',
          cssClass: 'secondary',
          handler: () => {}
        },
        {
          text: this.translateConfig.t('general.ok'),
          handler: async (values) => {
            const menuTitle = values.menuTitle;
            if (!menuTitle) {
              await this.appService.showToastWithDuration("You must provide a name for this menu", null, "danger");
              return;
            }
            try {
              await this.mealPlanService.addMenu(this.organization?.uid, this.activeUser.uid, {
                ...this.helperService.cleanObjectForFirebase({ ...toSave, title: menuTitle }),
                title: menuTitle,
                lastModifiedBy: this.activeUser.uid,
                lastModifiedDate: new Date().toISOString(),
              });
              this.appService.showToastWithDuration("Menu successfully saved to library", null, "success");
            } catch (error) {
              Sentry.captureException(error.originalError || error);
              this.appService.showToastWithDuration("Unable to save menu to library", null, "danger");
            }
          }
        }
      ]
    });
    await alert.present();
  }

  async editDate(event, type: "startDate" | "endDate") {
    const popover = await this.popoverCtrl.create({
      component: ProgramDatePopoverComponent,
      event: event,
      cssClass: 'popover-full',
      side: getProgramDatePopoverSide(event),
      componentProps: {
        initial: this.activeMealPlan[type],
        clear: type === "endDate",
        includeTime: true,
        dateType: type,
      }
    });
    await popover.present();
    const { role, data } = await popover.onDidDismiss();
    if(role === "cleared" || role === "set") {
      const toUpdate = data || null;
      this.activeMealPlan[type] = toUpdate;
      this.saveMealPlan();
    }
  }
}
